import { cx } from '@emotion/css';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { Accordion, AccordionSummary, Box, Link } from '@mui/material';
import type { HTMLAttributeAnchorTarget, ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { RouteParams } from '../../../configs/route.config';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_EMPTY_VALUE,
  LanguageLocale,
} from '../../../constants/common.constant';
import type { Entity } from '../../../models/common.model';
import type { RouteConfig } from '../../../models/route.model';
import type { BDTableData, TableMeta } from '../../../models/table/table.model';
import { format } from '../../../utils/date-time.utils';
import { makeRoutePath } from '../../../utils/route/route.utils';

export const dateRenderer = (
  value: string | Date | BDTableData,
  dateFormat = DEFAULT_DATE_FORMAT,
  locale: string = LanguageLocale.EN,
  timeZone?: string
): string => {
  // Using instanceof to check for Date values could produce unexpected results
  // if value is a malformed Date; consider restricting param types to string | BDTableData
  if (
    value &&
    value !== DEFAULT_EMPTY_VALUE &&
    (typeof value === 'string' || value instanceof Date)
  ) {
    return (
      (value && format(new Date(value), dateFormat, locale, timeZone)) ||
      DEFAULT_EMPTY_VALUE
    );
  }
  return DEFAULT_EMPTY_VALUE;
};

/**
 *
 * @param displayValue link text
 * @param href href to use for link
 * @param to react-router-dom value representing link destination; ignored if href is provided
 * @returns react link component
 */
export const getLinkRenderer = ({
  displayValue,
  href,
  to,
  alwaysLink = false,
  target,
}: {
  displayValue: string;
  href?: string;
  to?: string;
  alwaysLink?: boolean;
  target?: HTMLAttributeAnchorTarget;
}): JSX.Element => {
  if (alwaysLink || (displayValue && displayValue !== DEFAULT_EMPTY_VALUE)) {
    if (href) {
      return (
        <Link href={href} title={displayValue} target={target}>
          {displayValue}
        </Link>
      );
    } else if (to) {
      return (
        <Link
          to={to}
          title={displayValue}
          component={RouterLink}
          target={target}
        >
          {displayValue}
        </Link>
      );
    } else {
      return <>{displayValue}</>;
    }
  }
  return <>{DEFAULT_EMPTY_VALUE}</>;
};

export const EmailLinkRenderer = (value: string): JSX.Element => {
  return (
    <Box component="div" className="emailAddress">
      {getLinkRenderer({ displayValue: value, href: `mailto:${value}` })}
    </Box>
  );
};

export const EmailTextRenderer = (value: string): JSX.Element => {
  return (
    <Box component="div" className="emailAddress">
      {value}
    </Box>
  );
};

export type ParamAdapterType<T> = (
  value: T,
  tableMeta: TableMeta
) => RouteParams;

export const getRouteLinkRenderer = <ValueType,>({
  route,
  exact = false,
  valueAdapter = (value: ValueType) => `${value}`,
  paramAdapter = () => ({}),
  alwaysLink = false,
  disableRouting = false,
  target,
  withQueryParams = false,
}: {
  route: string | RouteConfig;
  exact?: boolean;
  valueAdapter?: (value: ValueType) => string;
  paramAdapter?: ParamAdapterType<ValueType>;
  alwaysLink?: boolean;
  disableRouting?: boolean;
  target?: HTMLAttributeAnchorTarget;
  withQueryParams?: boolean;
}): ((value: ValueType, tableMeta: TableMeta) => JSX.Element) => {
  const RouteLinkRenderer = (
    value: ValueType,
    tableMeta: TableMeta
  ): JSX.Element => {
    const displayValue = valueAdapter(value);
    const routeParams: RouteParams = paramAdapter(value, tableMeta);

    return getLinkRenderer({
      displayValue,
      to: !disableRouting
        ? makeRoutePath(route, routeParams, exact, withQueryParams)
        : undefined,
      alwaysLink,
      target,
    });
  };
  return RouteLinkRenderer;
};

export const entityNameRenderer = <T extends Entity>(
  value: T | BDTableData
): string => {
  if (typeof value !== 'string') {
    if (value?.name && typeof value.name !== 'object') {
      return value.name;
    }
  }
  return DEFAULT_EMPTY_VALUE;
};
